import FlagIcon from "@mui/icons-material/Flag";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import {
  Badge,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  debounce
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IControlledMutator, IDisable, ILoaded } from "sonobello.utilities.react";

import Modal from "../../../Components/Helpers/Modal";
import { EnhancedCustomerNote } from "../../../Models/CustomerNote";
import { INoteTagContentBuilder } from "../Types/INoteTagContentBuilder";
import { SeriesNoteTagsBuilder } from "../Types/NoteTagsConfiguration";
import PreviousCustomerNote from "../Types/PreviousCustomerNote";
import FullNoteEditor from "./FullNoteEditor";
import NoteTagsDisplay from "./NoteTagsDisplay";
import PreviousCustomerNoteCard from "./PreviousCustomerNote";

export const classNames = {
  textArea: "customerNotePanelTextArea",
  redFlagCounter: "customerNotePanelRedFlagCounter",
  fullNoteButton: "customerNotePanelFullNoteButton",
  fullNoteModal: "customerNotePanelFullNoteModal"
};

export interface ICustomerNotePanelProps<TNoteTagProps>
  extends IControlledMutator<EnhancedCustomerNote>,
    IDisable,
    ILoaded {
  /** The previous notes for the session's currently viewed customer. */
  previousNotes: PreviousCustomerNote[];
  /** The properties which inform the current note tags. */
  noteTagsProps: TNoteTagProps;
}

export interface ICustomerNotePanelConfig<TNoteTagProps> {
  /** The builder for note tag content. */
  noteTagsContentBuilder: INoteTagContentBuilder;
  /** The configuration which consumes the note tag props to yield the note tags state. */
  noteTagsConfiguration: SeriesNoteTagsBuilder<TNoteTagProps>;
}

const CustomerNotePanel = <TNoteTagProps,>({
  disabled,
  isLoading,
  previousNotes,
  noteTagsContentBuilder,
  noteTagsConfiguration,
  noteTagsProps,
  value,
  onChange
}: ICustomerNotePanelProps<TNoteTagProps> & ICustomerNotePanelConfig<TNoteTagProps>) => {
  const [localValue, setLocalValue] = useState(value.text);
  const [isOpen, setIsOpen] = useState(false);
  const valueRef = useRef(value);

  useEffect(() => {
    setLocalValue(value.text);
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const newTags = noteTagsConfiguration.getTags(noteTagsProps);
    if (
      noteTagsContentBuilder.getNoteTagContent(newTags) !==
      noteTagsContentBuilder.getNoteTagContent(valueRef.current.tags)
    )
      onChange({ ...valueRef.current, tags: noteTagsConfiguration.getTags(noteTagsProps) });
  }, [noteTagsConfiguration, noteTagsProps]);

  const debouncedOnChange = useCallback(
    debounce((text: string) => onChange({ ...valueRef.current, text }), 600),
    []
  );

  const handleChange = (text: string) => {
    debouncedOnChange(text);
    setLocalValue(text);
  };

  const redFlagCount = previousNotes.filter(n => n.isRedFlag).length;
  return (
    <>
      <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardHeader
          title="Guest Notes"
          titleTypographyProps={{ variant: "h6" }}
          action={
            <Grid container spacing={1}>
              {Boolean(redFlagCount) && (
                <Tooltip title={`${redFlagCount} Red Flag Note${redFlagCount === 1 ? "" : "s"}`}>
                  <Badge
                    className={classNames.redFlagCounter}
                    badgeContent={redFlagCount}
                    color="error"
                    sx={{ mt: 2, mr: disabled ? 1.5 : undefined }}
                  >
                    <FlagIcon color="action" />
                  </Badge>
                </Tooltip>
              )}
              {!disabled && (
                <Grid item>
                  <Tooltip title="Show Full Guest Note">
                    <IconButton className={classNames.fullNoteButton} onClick={() => setIsOpen(true)} color="secondary">
                      <SpeakerNotesIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          }
          sx={theme => ({ p: 1, backgroundColor: theme.palette.grey[100], boxShadow: 3 })}
        />
        <CardContent sx={{ p: 1, flexGrow: 1, overflow: "auto" }}>
          <Grid container direction="column" spacing={1}>
            {!disabled && (
              <>
                <Grid item sx={{ mt: 0.5 }}>
                  <NoteTagsDisplay value={value.tags} />
                </Grid>
                <Grid item>
                  <TextField
                    className={classNames.textArea}
                    label="Add Note"
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={15}
                    value={localValue}
                    variant="outlined"
                    onChange={({ target: { value: text } }) => handleChange(text)}
                  />
                </Grid>
              </>
            )}
            {isLoading ? (
              <Grid container alignItems="center" justifyContent="center">
                <Box p={3}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : (
              previousNotes.map((note, index) => {
                return (
                  <Grid item key={index}>
                    <PreviousCustomerNoteCard {...note} />
                  </Grid>
                );
              })
            )}
          </Grid>
        </CardContent>
      </Card>
      <Modal
        className={classNames.fullNoteModal}
        title="Full Guest Note"
        open={isOpen}
        handleConfirm={() => setIsOpen(false)}
      >
        <DialogContent sx={{ minWidth: "30rem", p: 0 }}>
          <FullNoteEditor
            tags={noteTagsContentBuilder.getNoteTagContent(value.tags)}
            value={value.text}
            onChange={text => onChange({ ...valueRef.current, text })}
          />
        </DialogContent>
      </Modal>
    </>
  );
};

export default CustomerNotePanel;
